import { useEffect, useState } from "react";
import DocumentName from "../../../components/CreateOrEditDocument/DocumentName/DocumentName";
import styles from "./StepOne.module.css";
import DocumentDescription from "../../../components/CreateOrEditDocument/DocumentDescription/DocumentDescription";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../api/api";
import ConfirmActionModal from "../../../components/ConfirmActionModal/ConfirmActionModal";
import SelectElement, {
  type SelectListItemType,
} from "../../../components/SelectElement/SelectElement";
import type { GetAllDepartmentsListType } from "../../../shared/types/departments.types";
import FileDropzone from "../../../components/FileDropzone/FileDropzone";
import { useFormik } from "formik";
import * as Yup from "yup";

type submitValuesType = {
  name: string;
  description: string;
  departmentId: string;
  departmentName: string;
  file: File;
};

type SavedDataType = Omit<submitValuesType, "file"> & { file: File | null };

type StepOneType = {
  onSubmit: (values: submitValuesType) => void;
  savedData: SavedDataType;
};

type InitialValuesType = {
  name: string;
  description: string;
  departmentId: string;
  departmentName: string;
  file: File | null;
};

const getValidationSchema = (context: { existingNames: string[] }) =>
  Yup.object({
    name: Yup.string()
      .required("Un nombre no puede estar vacío!")
      .trim()
      .test(
        "unique-name",
        "Ese nombre ya está cogido. Prueba con otro",
        function (value) {
          if (!value || !context.existingNames) return true;
          return !context.existingNames.includes(value);
        }
      ),
    description: Yup.string()
      .required("La descripción no puede estar en blanco!")
      .max(200, "El texto es demasiado largo!"),
    departmentId: Yup.string()
      .required("Debe elegir un departamento")
      .notOneOf(["1"], "Debe elegir un departamento"),
    file: Yup.mixed()
      .required("Tienes que descargar el archivo")
      .test(
        "file-exists",
        "Tienes que descargar el archivo",
        (value) => value !== null
      ),
  });

const StepOne = ({ onSubmit, savedData }: StepOneType) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [departments, setDepartments] =
    useState<GetAllDepartmentsListType | null>(null);
  const [existingNames, setExistingNames] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [departmentsData, templatesData] = await Promise.all([
        api.getAllDepartmentsList(),
        api.getAllDocumentsTypes(),
      ]);
      setDepartments(departmentsData);
      const mappedAndFilteredTypesNames = templatesData
        .map((type) => type.name)
        .filter((field) => field !== savedData.name);
      setExistingNames(mappedAndFilteredTypesNames);
      setIsLoaded(true);
    };
    fetchData();
  }, []);

  const initialValues: InitialValuesType =
    savedData === null
      ? {
          name: "",
          description: "",
          departmentId: "1",
          departmentName: "Departamento",
          file: null,
        }
      : {
          name: savedData.name,
          description: savedData.description,
          departmentId: savedData.departmentId,
          departmentName: savedData.departmentName,
          file: savedData.file || null,
        };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getValidationSchema({ existingNames }),
    onSubmit: async (values) => {
      if (values.file === null) return;
      console.log(values);
      onSubmit({ ...values, file: values.file });
    },
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/^\w/, (c) => c.toUpperCase());
    formik.setFieldValue("name", value);
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value.replace(/^\w/, (c) => c.toUpperCase());
    formik.setFieldValue("description", value);
  };

  const [isConfirmLeaveModalOpen, setIsConfirmLeaveModalOpen] = useState(false);

  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectListItemType>({
      id: formik.values.departmentId,
      element: <>{formik.values.departmentName}</>,
    });

  const selectDepartment = (option: SelectListItemType) => {
    formik.setFieldValue("departmentId", option.id);
    if (departments) {
      const selectedDept = departments.find((d) => d.id === option.id);
      formik.setFieldValue(
        "departmentName",
        selectedDept ? selectedDept.code : ""
      );
    }
    setSelectedDepartment(option);
  };

  const handleCancel = () => {
    const from = location.state?.from;
    navigate(from || "/");
  };

  if (!isLoaded) return null;

  return (
    <>
      <form className={styles.form} onSubmit={formik.handleSubmit}>
        <div className={styles.infoContainer}>
          <div className={styles.templateTextContainer}>
            <DocumentName
              inputValue={formik.values.name}
              handleNameInputChange={handleNameChange}
              errorElement={
                formik.touched.name &&
                formik.errors.name && (
                  <div className={styles.textWarning}>{formik.errors.name}</div>
                )
              }
            />
            <DocumentDescription
              errorElement={
                formik.touched.description &&
                formik.errors.description && (
                  <div className={styles.textWarning}>
                    {formik.errors.description}
                  </div>
                )
              }
              inputValue={formik.values.description}
              handleDescriptionInputChange={handleDescriptionChange}
            />
          </div>
          <div className={styles.dropzoneContainer}>
            <p className={styles.dropzoneText}>Sube un ejemplo del documento</p>
            {formik.touched.file && formik.errors.file && (
              <div className={styles.textWarning}>{formik.errors.file}</div>
            )}
            <FileDropzone
              file={formik.values.file}
              onChange={(file) => formik.setFieldValue("file", file)}
            />
          </div>
        </div>
        <div className={styles.propertiesContainer}>
          <div className={styles.departmentsContainer}>
            <h3 className={styles.departmentsContainerTitle}>
              Clasificación del documento
            </h3>
            <p className={styles.departmentsContainerText}>
              Selecciona el departamento competente de este documento
            </p>
            {formik.touched.departmentId && formik.errors.departmentId && (
              <div className={styles.textWarning}>
                {formik.errors.departmentId}
              </div>
            )}
            <SelectElement
              options={
                departments &&
                departments.map((d) => ({
                  id: d.id,
                  element: <p className={styles.selectListItem}>{d.code}</p>,
                }))
              }
              setSelected={selectDepartment}
              selected={selectedDepartment}
              className={styles.selectElement}
              dropdownItemClassName={styles.selectElementDropdownItem}
              dropdownClassName={styles.selectElementDropdown}
            />
          </div>

          <div className={styles.formActionsButtonsContainer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setIsConfirmLeaveModalOpen(true)}
            >
              Cancelar
            </button>
            <button className={styles.submitButton} type="submit">
              Siguiente
            </button>
          </div>
        </div>
      </form>

      <ConfirmActionModal
        isOpen={isConfirmLeaveModalOpen}
        onRequestClose={() => setIsConfirmLeaveModalOpen(false)}
        modalTitle="Cancelar proceso"
        modalText="¿Está seguro que desea cancelar este proceso?, se perderán los cambios no guardados"
        cancelActionText="Continuar"
        cancelActionHandler={() => setIsConfirmLeaveModalOpen(false)}
        confirmActionText="Cancelar proceso"
        confirmActionHandler={handleCancel}
      />
    </>
  );
};

export default StepOne;
